import { useState, useEffect, useRef, useCallback, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import liveStreamService, { LiveStream as LiveStreamType } from '../../services/liveStreamService'
import { Video, VideoOff, Mic, MicOff, PhoneOff, Users, Clock, Settings } from 'lucide-react'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'

// Stream Controls Component
const StreamControls = memo(({
  cameraEnabled,
  micEnabled,
  toggleCamera,
  toggleMic,
  isLive,
  isConnecting,
  title,
  startStream,
  endStream
}: {
  cameraEnabled: boolean
  micEnabled: boolean
  toggleCamera: () => void
  toggleMic: () => void
  isLive: boolean
  isConnecting: boolean
  title: string
  startStream: () => void
  endStream: () => void
}) => {
  return (
    <div className="flex items-center justify-center gap-4 mt-4">
      <button
        onClick={toggleCamera}
        className={`p-3 rounded-full transition-colors ${
          cameraEnabled ? 'bg-gray-200 hover:bg-gray-300' : 'bg-red-500 hover:bg-red-600'
        }`}
        aria-label={cameraEnabled ? 'Turn off camera' : 'Turn on camera'}
      >
        {cameraEnabled ? <Video className="w-5 h-5 text-gray-700" /> : <VideoOff className="w-5 h-5 text-white" />}
      </button>

      <button
        onClick={toggleMic}
        className={`p-3 rounded-full transition-colors ${
          micEnabled ? 'bg-gray-200 hover:bg-gray-300' : 'bg-red-500 hover:bg-red-600'
        }`}
        aria-label={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
      >
        {micEnabled ? <Mic className="w-5 h-5 text-gray-700" /> : <MicOff className="w-5 h-5 text-white" />}
      </button>

      {!isLive ? (
        <GovButton
          onClick={startStream}
          disabled={isConnecting || !title.trim()}
          className={`${(isConnecting || !title.trim()) ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isConnecting ? 'Starting...' : 'Go Live'}
        </GovButton>
      ) : (
        <GovButton variant="secondary" onClick={endStream} className="bg-red-500 text-white hover:bg-red-600 border-red-500">
          <PhoneOff className="w-4 h-4 inline mr-1" />
          End Stream
        </GovButton>
      )}
    </div>
  )
})

StreamControls.displayName = 'StreamControls'

// Video Player Component
const VideoPlayer = memo(({
  videoRef,
  cameraEnabled,
  isLive,
  duration,
  viewerCount
}: {
  videoRef: React.RefObject<HTMLVideoElement>
  cameraEnabled: boolean
  isLive: boolean
  duration: number
  viewerCount: number
}) => {
  const formatDuration = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={`w-full h-full object-cover ${!cameraEnabled ? 'hidden' : ''}`}
      />
      {!cameraEnabled && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
          <VideoOff className="w-16 h-16 text-gray-600" />
        </div>
      )}

      {isLive && (
        <>
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white text-sm font-medium rounded">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              LIVE
            </span>
            <span className="px-2 py-1 bg-black/60 text-white text-sm rounded">
              {formatDuration(duration)}
            </span>
          </div>

          <div className="absolute top-4 right-4 flex items-center gap-1 px-2 py-1 bg-black/60 text-white text-sm rounded">
            <Users className="w-4 h-4" />
            {viewerCount}
          </div>
        </>
      )}
    </div>
  )
})

VideoPlayer.displayName = 'VideoPlayer'

// Stream Settings Component
const StreamSettings = memo(({
  title,
  setTitle,
  description,
  setDescription
}: {
  title: string
  setTitle: (value: string) => void
  description: string
  setDescription: (value: string) => void
}) => {
  return (
    <GovCard className="mt-6">
      <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
        <Settings className="w-5 h-5 text-blue-600" />
        Stream Settings
      </h2>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter stream title..."
            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            maxLength={100}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What's your stream about?"
            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
            rows={3}
            maxLength={500}
          />
        </div>
      </div>
    </GovCard>
  )
})

StreamSettings.displayName = 'StreamSettings'

// Stream Stats Component
const StreamStats = memo(({
  duration,
  viewerCount,
  likeCount
}: {
  duration: number
  viewerCount: number
  likeCount: number
}) => {
  const formatDuration = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <GovCard className="mt-4">
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-2xl font-bold text-gray-900">{formatDuration(duration)}</p>
          <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
            <Clock className="w-3 h-3" /> Duration
          </p>
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{viewerCount}</p>
          <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
            <Users className="w-3 h-3" /> Viewers
          </p>
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{likeCount}</p>
          <p className="text-xs text-gray-500">Likes</p>
        </div>
      </div>
    </GovCard>
  )
})

StreamStats.displayName = 'StreamStats'

// Stream Info Sidebar
const StreamInfoSidebar = memo(({
  stream,
  user,
  isLive,
  duration
}: {
  stream: LiveStreamType | null
  user: any
  isLive: boolean
  duration: number
}) => {
  if (isLive && stream) {
    return (
      <GovCard>
        <h3 className="font-medium mb-2 text-gray-900">{stream.title}</h3>
        {stream.description && (
          <p className="text-sm text-gray-600 mb-4">{stream.description}</p>
        )}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <img
            src={user?.profileImage || '/default-avatar.svg'}
            alt={user?.firstName}
            className="w-8 h-8 rounded-full object-cover"
          />
          <span>{user?.firstName} {user?.lastName}</span>
        </div>
        <div className="mt-4 text-xs text-gray-500">
          <p>Max duration: {Math.floor((stream.maxDurationSeconds || 3600) / 60)} minutes</p>
          <p>Remaining: {Math.floor(((stream.maxDurationSeconds || 3600) - duration) / 60)} minutes</p>
        </div>
      </GovCard>
    )
  }

  return (
    <GovCard>
      <h3 className="font-medium mb-4 text-gray-900">Tips for a Great Stream</h3>
      <ul className="space-y-2 text-sm text-gray-600">
        <li className="flex items-start gap-2">
          <span className="text-blue-600">•</span>
          Use a descriptive title
        </li>
        <li className="flex items-start gap-2">
          <span className="text-blue-600">•</span>
          Check your lighting
        </li>
        <li className="flex items-start gap-2">
          <span className="text-blue-600">•</span>
          Engage with viewers
        </li>
        <li className="flex items-start gap-2">
          <span className="text-blue-600">•</span>
          Keep stream under 1 hour
        </li>
        <li className="flex items-start gap-2">
          <span className="text-blue-600">•</span>
          Use a stable internet connection
        </li>
      </ul>
    </GovCard>
  )
})

StreamInfoSidebar.displayName = 'StreamInfoSidebar'

// Main Component
const LiveStream = memo(() => {
  const navigate = useNavigate()
  const { user } = useAuthStore()

  // Stream state
  const [stream, setStream] = useState<LiveStreamType | null>(null)
  const [isLive, setIsLive] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)

  // Media state
  const [cameraEnabled, setCameraEnabled] = useState(true)
  const [micEnabled, setMicEnabled] = useState(true)

  // Stream settings
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [showSettings, setShowSettings] = useState(true)

  // Stream stats
  const [viewerCount, setViewerCount] = useState(0)
  const [duration, setDuration] = useState(0)
  const [likeCount, setLikeCount] = useState(0)

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const peerConnectionsRef = useRef<Map<string, RTCPeerConnection>>(new Map())

  // WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    const isDev = import.meta.env.DEV
    if (isDev) {
      return 'ws://localhost:8091/ws/live-stream'
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    return `${protocol}//${host}/ws/live-stream`
  }, [])

  const ICE_SERVERS = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' }
  ]

  // Initialize camera
  const initCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720, facingMode: 'user' },
        audio: true
      })
      mediaStreamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }
    } catch (error) {
      console.error('Error accessing camera:', error)
      alert('Unable to access camera. Please check permissions.')
    }
  }, [])

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback(async (data: any) => {
    switch (data.type) {
      case 'viewer_joined':
        setViewerCount(data.viewerCount || 0)
        await createOfferForViewer(data.viewerSessionId)
        break

      case 'answer':
        await handleAnswer(data.viewerSessionId, data.answer)
        break

      case 'ice_candidate':
        await handleIceCandidate(data.senderSessionId, data.candidate)
        break

      case 'viewer_left':
        setViewerCount(data.viewerCount || 0)
        peerConnectionsRef.current.delete(data.viewerSessionId)
        break

      case 'like':
        setLikeCount(prev => prev + 1)
        break

      case 'viewer_count':
        setViewerCount(data.count)
        break
    }
  }, [])

  // Create offer for new viewer
  const createOfferForViewer = useCallback(async (viewerSessionId: string) => {
    if (!mediaStreamRef.current) return

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS })
    peerConnectionsRef.current.set(viewerSessionId, pc)

    mediaStreamRef.current.getTracks().forEach(track => {
      pc.addTrack(track, mediaStreamRef.current!)
    })

    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice_candidate',
          streamId: stream?.id,
          candidate: event.candidate.toJSON()
        }))
      }
    }

    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'offer',
        streamId: stream?.id,
        offer: offer.sdp
      }))
    }
  }, [stream?.id])

  // Handle answer from viewer
  const handleAnswer = useCallback(async (viewerSessionId: string, answer: RTCSessionDescriptionInit) => {
    const pc = peerConnectionsRef.current.get(viewerSessionId)
    if (pc) {
      await pc.setRemoteDescription(new RTCSessionDescription(answer))
    }
  }, [])

  // Handle ICE candidate
  const handleIceCandidate = useCallback(async (viewerSessionId: string, candidate: RTCIceCandidateInit) => {
    const pc = peerConnectionsRef.current.get(viewerSessionId)
    if (pc) {
      await pc.addIceCandidate(new RTCIceCandidate(candidate))
    }
  }, [])

  // Connect to WebSocket
  const connectWebSocket = useCallback((streamId: number) => {
    const wsUrl = `${getWebSocketUrl()}?streamId=${streamId}&role=broadcaster&userId=${user?.id}`
    wsRef.current = new WebSocket(wsUrl)

    wsRef.current.onopen = () => {
      console.log('WebSocket connected')
      wsRef.current?.send(JSON.stringify({
        type: 'broadcaster_join',
        streamId,
        userId: user?.id
      }))
    }

    wsRef.current.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data)
        handleWebSocketMessage(data)
      } catch (error) {
        console.error('Error parsing WebSocket message:', error)
      }
    }

    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error)
    }

    wsRef.current.onclose = () => {
      console.log('WebSocket closed')
    }
  }, [getWebSocketUrl, user?.id, handleWebSocketMessage])

  // Start streaming
  const startStream = useCallback(async () => {
    if (!title.trim()) {
      alert('Please enter a title for your stream')
      return
    }

    setIsConnecting(true)
    try {
      const response = await liveStreamService.startStream(title, description, true)
      if (response.success) {
        const streamData = response.data.stream || response.data
        setStream(streamData)
        setIsLive(true)
        setShowSettings(false)

        connectWebSocket(streamData.id)

        durationIntervalRef.current = setInterval(() => {
          setDuration(prev => prev + 1)
        }, 1000)
      }
    } catch (error) {
      console.error('Error starting stream:', error)
      alert('Failed to start stream. Please try again.')
    } finally {
      setIsConnecting(false)
    }
  }, [title, description, connectWebSocket])

  // End stream
  const endStream = useCallback(async () => {
    if (!stream) return

    try {
      await liveStreamService.endStream(stream.id)

      peerConnectionsRef.current.forEach(pc => pc.close())
      peerConnectionsRef.current.clear()

      if (wsRef.current) {
        wsRef.current.send(JSON.stringify({ type: 'broadcaster_leave', streamId: stream.id }))
        wsRef.current.close()
      }

      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current)
      }

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop())
      }

      navigate('/live')
    } catch (error) {
      console.error('Error ending stream:', error)
      navigate('/live')
    }
  }, [stream, navigate])

  // Toggle camera
  const toggleCamera = useCallback(() => {
    if (mediaStreamRef.current) {
      const videoTrack = mediaStreamRef.current.getVideoTracks()[0]
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled
        setCameraEnabled(videoTrack.enabled)
      }
    }
  }, [])

  // Toggle microphone
  const toggleMic = useCallback(() => {
    if (mediaStreamRef.current) {
      const audioTrack = mediaStreamRef.current.getAudioTracks()[0]
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled
        setMicEnabled(audioTrack.enabled)
      }
    }
  }, [])

  // Initialize on mount
  useEffect(() => {
    initCamera()
    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop())
      }
      if (wsRef.current) {
        wsRef.current.close()
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current)
      }
    }
  }, [initCamera])

  // Check for existing active stream
  useEffect(() => {
    const checkActiveStream = async () => {
      try {
        const response = await liveStreamService.getMyActiveStream()
        if (response.success && response.data?.isLive && response.data?.stream) {
          setStream(response.data.stream)
          setIsLive(true)
          setShowSettings(false)
          connectWebSocket(response.data.stream.id)
          durationIntervalRef.current = setInterval(() => {
            setDuration(prev => prev + 1)
          }, 1000)
        }
      } catch (error) {
        console.log('No active stream')
      }
    }
    checkActiveStream()
  }, [connectWebSocket])

  return (
    <GovLayout showSidebar={false}>
      <div className="max-w-6xl mx-auto">
        <GovPageTitle title="Live Stream" subtitle="Broadcast your session to raise TB awareness" icon={Video} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Video Preview / Stream */}
          <div className="lg:col-span-2">
            <VideoPlayer
              videoRef={videoRef}
              cameraEnabled={cameraEnabled}
              isLive={isLive}
              duration={duration}
              viewerCount={viewerCount}
            />

            <StreamControls
              cameraEnabled={cameraEnabled}
              micEnabled={micEnabled}
              toggleCamera={toggleCamera}
              toggleMic={toggleMic}
              isLive={isLive}
              isConnecting={isConnecting}
              title={title}
              startStream={startStream}
              endStream={endStream}
            />

            {!isLive && showSettings && (
              <StreamSettings
                title={title}
                setTitle={setTitle}
                description={description}
                setDescription={setDescription}
              />
            )}

            {isLive && (
              <StreamStats
                duration={duration}
                viewerCount={viewerCount}
                likeCount={likeCount}
              />
            )}
          </div>

          {/* Stream info sidebar */}
          <div className="lg:col-span-1">
            <StreamInfoSidebar
              stream={stream}
              user={user}
              isLive={isLive}
              duration={duration}
            />
          </div>
        </div>
      </div>
    </GovLayout>
  )
})

LiveStream.displayName = 'LiveStream'

export default LiveStream